import { useCallback, useMemo, useRef } from 'react';

export interface UseOCClaimScrollPaginationParams {
  loadedCount: number;
  totalRows: number;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
}

/**
 * Scroll/load-more helpers for OC claim tabs using ScrollPagination.
 * Prevents duplicate fetches and aligns hasMore with loaded row count.
 */
export const useOCClaimScrollPagination = ({
  loadedCount,
  totalRows,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: UseOCClaimScrollPaginationParams) => {
  const isLoadingMoreRef = useRef(false);

  const scrollHasMore = useMemo(() => {
    if (totalRows > 0 && loadedCount >= totalRows) {
      return false;
    }
    return isFetchingNextPage ? false : (hasNextPage ?? false);
  }, [loadedCount, totalRows, hasNextPage, isFetchingNextPage]);

  const handleNextPage = useCallback(() => {
    if (isLoadingMoreRef.current) {
      return;
    }
    if (totalRows > 0 && loadedCount >= totalRows) {
      return;
    }
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    isLoadingMoreRef.current = true;
    void fetchNextPage().finally(() => {
      isLoadingMoreRef.current = false;
    });
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, loadedCount, totalRows]);

  return { handleNextPage, scrollHasMore };
};
